import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { SeoService } from '@core/services/seo.service';
import { langFromLocale } from '@core/utils/utils';
import { Links, Metas } from '@features/profile-unlogged/seo-vars';
import { Profile } from '@models/profile/profile';
import { ProfileSectionEnum } from '@models/profile/profile-section.enum';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { LinkRel, TypesEnum } from '@routing/directories/directories-routing.enum';
import { ProfileState } from '@store/ngxs-profile/profile.state';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-view-unlogged',
  templateUrl: './view.component.html',
  styleUrls: ['./view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewComponent implements OnInit, OnDestroy {
  @Select(ProfileState.loading) loading$: Observable<Record<ProfileSectionEnum, boolean>>;

  profile: Profile;
  private metaElements: HTMLMetaElement[] = [];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly store: Store,
    private readonly seoService: SeoService,
    private readonly metaService: Meta,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        map((paramMap) => paramMap.get('profileId')),
        switchMap((profileId) => this.store.selectOnce(ProfileState.profile(profileId))),
        tap(this.initSeoData.bind(this)),
        untilDestroyed(this)
      )
      .subscribe((profile) => {
        this.profile = profile;
        this.cdr.markForCheck();
      });
  }

  private initSeoData(profile: Profile): void {
    const lang = langFromLocale(profile.locale);
    this.seoService.createLinkForCanonicalURL(lang, TypesEnum.profile, { id: profile.id });
    this.seoService.addLinkTags(Links);

    const firstname = profile.firstName ? profile.firstName : '';
    const lastname = profile.lastName ? ` ${profile.lastName}` : '';

    this.metaElements = [
      ...[
        { name: 'robots', content: profile.allowIndex ? 'index,follow' : 'noindex,nofollow' },
        { name: 'author', content: `${firstname}${lastname}` },
        { itemprop: 'name', content: `${firstname}${lastname} - WIZBII` },
        { property: 'og:url', content: `https://www.wizbii.com/profile/${profile.id}` },
        { property: 'og:image', content: `https://images.wizbii.com/profile/${profile.id}/avatar/large@2x` },
      ].map((tag) => this.metaService.addTag(tag)),
      ...this.metaService.addTags(Metas),
    ];
  }

  ngOnDestroy(): void {
    this.seoService.removeLinkWithRel(LinkRel.Canonical);

    Links.forEach((link) => {
      this.seoService.removeLinkTag(link);
    });

    this.metaElements.forEach((meta) => {
      this.metaService.removeTagElement(meta);
    });
  }
}
